import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { useFormik } from "formik";
import * as yup from "yup";
import cr from "../../lib/request";
import ComponentSpinner from "../ComponentSpinner";
import RequiredForm from "../RequiredForm";
import { useNavigate } from "react-router-dom";
import { iconSize } from "../../const/sizes";
import useScreenWidth from "../../lib/useGetScreenWidth";
import FileUploader from "../FileUploader";

export default function TambahBlogPostModal() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const sw = useScreenWidth();

  const [mainImagePreviewUrl, setMainImagePreviewUrl] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();

  const formik = useFormik({
    validateOnChange: false,

    initialValues: {
      title: "",
      category: "",
      description: "",
      image: [] as File[] | null,
    },

    validationSchema: yup.object().shape({
      title: yup.string().required("Judul Blog harus diisi"),
      category: yup.number().required("Kategori Blog harus dipilih"),
      description: yup.string().required("Deskripsi Blog harus diisi"),
      image: yup
        .array()
        .min(1, "Gambar Thumbnail harus diunggah")
        .required("Gambar Thumbnail harus diunggah")
        .test(
          "fileType",
          "Hanya file JPG, PNG, HEIC yang diperbolehkan",
          (value) =>
            value.every(
              (file) =>
                file.type === "image/jpeg" ||
                file.type === "image/jpg" ||
                file.type === "image/png" ||
                file.type === "image/HEIC"
            )
        ),
    }),

    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      const url = `api/blog/post/store`;
      const payload = new FormData();
      payload.append("title", values.title);
      payload.append("category", values.category);
      payload.append("description", values.description);
      if (values.image) {
        values.image.forEach((file) => {
          payload.append("image", file);
        });
      }

      cr.post(url, payload)
        .then((r) => {
          if (r.status === 201) {
            toast({
              title: r.data.message,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
            navigate(`/blog/post/${r.data.data.id}/edit`);
          }
        })
        .catch((e) => {
          console.log(e);
          toast({
            title: e.response.data.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const [categories, setCategories] = useState<any[] | null>(null);
  const [getCategoriesLoading, setGetCategoriesLoading] =
    useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setGetCategoriesLoading(true);
      const url = `api/blog/allcategories`;
      cr.get(url)
        .then((r) => {
          console.log(r.data);
          setCategories(r.data.data);
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          setGetCategoriesLoading(false);
        });
    }
  }, [isOpen]);

  return (
    <>
      {sw >= 500 ? (
        <Button
          colorScheme="p"
          className="clicky"
          leftIcon={<Icon as={Plus} fontSize={iconSize} weight="bold" />}
          pl={3}
          onClick={onOpen}
          flexShrink={0}
        >
          Tambah
        </Button>
      ) : (
        <IconButton
          colorScheme="p"
          className="clicky"
          aria-label="tambahKategoriBlog"
          icon={<Icon as={Plus} fontSize={iconSize} />}
          onClick={onOpen}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={"lg"}
        scrollBehavior="inside"
      >
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>Buat Blog Baru</ModalHeader>

          {!getCategoriesLoading && categories ? (
            <>
              <ModalBody>
                <form id="createBlogPostForm" onSubmit={formik.handleSubmit}>
                  <FormControl
                    mb={4}
                    isInvalid={formik.errors.title ? true : false}
                  >
                    <FormLabel>
                      Judul
                      <RequiredForm />
                    </FormLabel>
                    <Textarea
                      name="title"
                      placeholder="Masukkan judul blog"
                      onChange={formik.handleChange}
                      value={formik.values.title}
                    />
                    <FormErrorMessage>{formik.errors.title}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    mb={4}
                    isInvalid={formik.errors.category ? true : false}
                  >
                    <FormLabel>
                      Kategori
                      <RequiredForm />
                    </FormLabel>

                    <Select
                      name="category"
                      placeholder="Pilih kategori blog"
                      onChange={formik.handleChange}
                      value={formik.values.category}
                    >
                      {categories?.map((category, i) => (
                        <option key={i} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {formik.errors.category}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    mb={4}
                    isInvalid={formik.errors.description ? true : false}
                  >
                    <FormLabel>
                      Deksripsi
                      <RequiredForm />
                    </FormLabel>
                    <Textarea
                      name="description"
                      placeholder="Masukkan deskripsi singkat blog"
                      onChange={formik.handleChange}
                      value={formik.values.description}
                    />
                    <FormErrorMessage>
                      {formik.errors.description}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formik.errors.image ? true : false}>
                    <FormLabel>
                      Gambar Thumbnail
                      <RequiredForm />
                    </FormLabel>
                    <FileUploader
                      name="image"
                      formik={formik}
                      setPreviewUrl={setMainImagePreviewUrl}
                      accept=".jpg, .jpeg, .png, .HEIC, .svg"
                    />
                    {mainImagePreviewUrl ? (
                      <Image
                        borderRadius={8}
                        src={mainImagePreviewUrl}
                        mt={4}
                        w={"100%"}
                      />
                    ) : (
                      <Text fontSize={"small"} opacity={0.5} mt={1}>
                        Preview akan terlihat dibawah setelah mengunggah
                      </Text>
                    )}
                    <FormErrorMessage>{formik.errors.image}</FormErrorMessage>
                  </FormControl>
                </form>
              </ModalBody>

              <ModalFooter gap={2}>
                <Button className="btn clicky" onClick={onClose}>
                  Batal
                </Button>

                <Button
                  type="submit"
                  form="createBlogPostForm"
                  colorScheme="p"
                  className="clicky"
                  isLoading={loading}
                >
                  Simpan & Lanjutkan
                </Button>
              </ModalFooter>
            </>
          ) : (
            <ComponentSpinner h="calc(154px + 88px)" />
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
